import { inject, Injectable } from '@angular/core'
import { APIService, TruckSnapshot } from 'projects/appsync/src/public-api'
import { MapLocation } from 'projects/commons/src/lib/interfaces/location'
import { LocationHelpersService } from 'projects/commons/src/lib/services/location-helpers.service'
import { MixingHelpersService } from 'projects/commons/src/lib/services/mixing-helpers.service'
import { TruckHelpersService } from 'projects/commons/src/lib/services/truck-helpers.service'
import { MixingEvent } from 'projects/commons/src/lib/types/mixing'
import { Observable, of } from 'rxjs'
import { catchError, filter, map } from 'rxjs/operators'

export type MixingData = {
  mixingEvent: MixingEvent
  registration: string
  drumVectorRpm: number
}

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private apiService = inject(APIService)
  private mixingHelpers = inject(MixingHelpersService)
  private truckHelpers = inject(TruckHelpersService)
  private locationHelpers = inject(LocationHelpersService)

  constructor() {}

  private latestSnapshot: TruckSnapshot | null = null

  public async latestBatchSpeedPattern(
    rmcProvider: string,
    plantCode: string,
    loadingPoint: string,
  ): Promise<MixingData | null> {
    const response = await this.apiService.GetLoadingPointTruckSnapshots(
      rmcProvider,
      plantCode,
      loadingPoint,
    )
    if (response?.items?.length && response.items.length > 0) {
      const trucks = (response.items as TruckSnapshot[]).map((snapshot) =>
        this.truckHelpers.cleanTruckInfo<TruckSnapshot>(snapshot),
      )
      //.map((item) => this.truckFromSnapshot(item))
      const relevantTrucks = trucks.filter(
        (truck) =>
          // truck.isActiveNow &&
          truck.batchSpeedPattern && truck.batchSpeedPatternTime,
      )
      console.log(relevantTrucks.length + ' relevant trucks')
      const sortedTrucks = relevantTrucks.sort((a, b) =>
        a.batchSpeedPatternTime > b.batchSpeedPatternTime ? -1 : 1,
      )

      console.log('sortedTrucks', sortedTrucks)
      if (sortedTrucks.length > 0) {
        const truck = sortedTrucks[0]
        // extract the relevant info and store the latest snapshot
        const { __typename, ...update } = truck
        this.latestSnapshot = update as TruckSnapshot
        return {
          mixingEvent: this.mixingHelpers.parseMixingPattern(
            truck.batchSpeedPattern,
            truck.batchSpeedPatternTime,
            this.isDate(truck.datetimeBatch) &&
              this.isDate(truck.leftBatchPlant) &&
              truck.datetimeBatch < truck.leftBatchPlant,
          ),
          registration: truck.truckPlate,
          drumVectorRpm: truck.drumVectorRpm,
        } as MixingData
      } else {
        console.log(
          'latestBatchSpeedPatternTruckSnapshot. ignoring inactive truck',
        )
      }
    }
    return null
  }

  public loadingPointSpeedPatternUpdates(
    rmcProvider: string,
    plantCode: string,
    loadingPoint: string,
  ): Observable<MixingData> {
    return this.apiService
      .SubscribeLoadingPointSpeedPatternListener(
        rmcProvider,
        plantCode,
        loadingPoint,
      )
      .pipe(
        catchError((e) => {
          console.log(
            'Error calling SubscribeLoadingPointSpeedPatternListener',
            e,
          )
          return of(null)
        }),
        map(
          (result) =>
            result?.data
              ?.subscribeLoadingPointSpeedPattern as unknown as TruckSnapshot,
        ),
        filter((update: TruckSnapshot) => {
          console.log('loadingPointSpeedPatternUpdates filter', update)
          // first check there is a latest snapshot
          if (!update) {
            return false
          }
          if (!this.latestSnapshot) {
            // nothing to compare to
            return true
          }

          const sameTruck = this.latestSnapshot.imei === update.imei
          const isLaterSystem =
            update.systemDatetimeUtcStr &&
            this.latestSnapshot?.systemDatetimeUtcStr
              ? update.systemDatetimeUtcStr >
                this.latestSnapshot?.systemDatetimeUtcStr
              : true
          const diffDrumSpeed =
            this.latestSnapshot.drumVectorRpm !== update.drumVectorRpm

          // DRUM SPEED CHECK
          // will get drumspeed updates from all trucks which were last batched here
          // only want listen to same truck with later system time
          const updateDrumSpeed = sameTruck && isLaterSystem && diffDrumSpeed

          // SPEED PATTERN CHECK
          // if there is a speed pattern update, it doesnt have to be from the same truck
          const updateSpeedPattern =
            update.batchSpeedPatternTime &&
            this.latestSnapshot?.batchSpeedPatternTime
              ? update.batchSpeedPatternTime >
                this.latestSnapshot.batchSpeedPatternTime
              : true

          return updateDrumSpeed || updateSpeedPattern
        }),
        map((update: TruckSnapshot) => {
          console.log('loadingPointSpeedPatternUpdates map the update', update)

          // store the latest snapshot
          this.latestSnapshot = update

          return {
            mixingEvent: this.mixingHelpers.parseMixingPattern(
              update.batchSpeedPattern,
              update.batchSpeedPatternTime,
              this.isDate(update.datetimeBatch) &&
                this.isDate(update.leftBatchPlant) &&
                update.datetimeBatch < update.leftBatchPlant,
            ),
            registration: update.truckPlate,
            drumVectorRpm: update.drumVectorRpm,
          } as MixingData
        }),
      )
  }

  public async getBatchPlant(
    rmcProvider: string,
    ownerId: string,
  ): Promise<MapLocation | null> {
    const response =
      await this.apiService.LocationSvcGetLocationsForRMCProvider(
        rmcProvider,
        undefined,
        ownerId,
      )
    const item =
      response && response.items && response.items.length > 0
        ? response.items[0]
        : null
    if (!item) {
      return null
    }
    return this.locationHelpers.locationToMapLocation(item)
  }

  public batchPlantName(rmcProvider: string, plantCode: string) {}

  private isDate(timestamp: string) {
    if (!timestamp) {
      return false
    }
    return !!Date.parse(timestamp)
  }
}
